import { faCube } from '@fortawesome/free-solid-svg-icons';
import { Column } from 'primereact/column';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import useAxiosHook from '../../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../../hooks/useEndpointGuard';
import usePageTitle from '../../../../hooks/usePageTitle';
import useRouteDialog from '../../../../hooks/useRouteDialog';
import useTableColumns from '../../../../hooks/useTableColumns';
import useTableState from '../../../../hooks/useTableState';
import { WithPaginationAndSummary } from '../../../../types/api';
import { EntityIdRouteParams } from '../../../../types/routing';
import * as ordersGuards from '../../../../utils/constants/auth/orders';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../../utils/constants/routePaths';
import { queryString } from '../../../../utils/helpers/http';
import { httpQueryObject } from '../../../../utils/helpers/misc';
import Table from '../../../DataTable/Table/Table';
import Flex from '../../../layout/flex/Flex';
import MainContent from '../../../layout/flex/MainContent';
import Filters from '../../Components/Filters/Filters';
import HeaderPages from '../../Components/HeaderPages/HeaderPages';
import EditIncompleteDialog from './Dialogs/Edit/EditIncompleteDialog';
import ViewIncompleteOrderDialog from './Dialogs/View/ViewIncompleteOrderDialog';
import {
  IncompleteOrders,
  additionalColumnProperties,
  getColumnHeadersMap,
  tableStorageKey,
} from './Incomplete.functions';
import useTableFilters from './useTableFilters';

function Incomplete(): JSX.Element {
  const { id } = useParams<EntityIdRouteParams>();
  const { t } = useTranslation();
  usePageTitle(t('Incomplete'));
  const [doubleClick, setIsDoubleClick] = useState(false);
  const [action, setAction] = useState<string>('');
  const readGuard = useEndpointGuard(ordersGuards.readOrder);
  const editGuard = useEndpointGuard(ordersGuards.editOrder);
  const [caller, setCaller] =
    useState<'group-actions' | 'context-menu'>('context-menu');

  const {
    tableRef,
    page,
    setPage,
    limit,
    setLimit,
    sortField,
    sortOrder,
    selectionMultiple,
    setSortField,
    setSortOrder,
    setSelectionMultiple,
    contextMenuSelection,
    setContextMenuSelection,
  } = useTableState<IncompleteOrders>(tableStorageKey);

  const { headerFiltersCount, filters, resetAllFilters, httpFiltersObj } =
    useTableFilters(page, setPage!, limit);

  const { data, isLoading, reload, error } = useAxiosHook<
    WithPaginationAndSummary<IncompleteOrders[], { redemption: string }>
  >({
    url:
      '/v2/address/incomplete' + queryString(httpQueryObject(httpFiltersObj)),
  });

  const {
    show: showEditDialog,
    hide: hideEditDialog,
    isVisible: isEditDialogVisible,
  } = useRouteDialog(
    RoutePaths.IncompleteOrder,
    constructIdRoute(
      RoutePaths.IncompleteEditOrder,
      id ?? contextMenuSelection?.orderId
    )
  );

  const {
    show: showViewActiveOrderDialog,
    hide: hideViewActiveOrderDialog,
    isVisible: isViewActiveOrderDialogVisible,
  } = useRouteDialog(
    RoutePaths.IncompleteOrder,
    constructIdRoute(
      RoutePaths.IncompleteViewOrder,
      id ?? contextMenuSelection?.orderId
    )
  );

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const { selectedColumns, setSelectedColumns, columnOptions, columns } =
    useTableColumns(
      page,
      limit,
      'orders_incompleteOrders',
      columnHeadersMap,
      columnHeadersMap,
      (c) =>
        additionalColumnProperties(
          t,
          c as keyof typeof columnHeadersMap,
          setContextMenuSelection,
          setAction,
          setCaller,
          readGuard,
          editGuard
        )
    );

  const finalColumns = useMemo<JSX.Element[]>(
    () => [
      ...columns,
      <Column
        key="action-column"
        header={t('Actions')}
        field="actions"
        frozen
        alignFrozen="right"
        {...additionalColumnProperties(
          t,
          'actions',
          setContextMenuSelection,
          setAction,
          setCaller,
          readGuard,
          editGuard
        )}
      />,
    ],
    [
      columns,
      t,
      setContextMenuSelection,
      setAction,
      setCaller,
      readGuard,
      editGuard,
    ]
  );

  function handleFromViewToEdit() {
    showEditDialog();
  }

  useEffect(() => {
    if (doubleClick && contextMenuSelection) {
      showViewActiveOrderDialog();
      setIsDoubleClick(false);
    }
  }, [contextMenuSelection, doubleClick, showViewActiveOrderDialog]);

  useEffect(() => {
    if (!readGuard) {
      hideViewActiveOrderDialog();
    }
  }, [hideViewActiveOrderDialog, readGuard]);

  useEffect(() => {
    if (!editGuard) {
      hideEditDialog();
    }
  }, [editGuard, hideEditDialog]);

  useEffect(() => {
    if (action && contextMenuSelection) {
      setCaller('context-menu');

      if (action === 'view-details') {
        showViewActiveOrderDialog();
      }
      if (action === 'edit') {
        showEditDialog();
      }

      setAction('');
    }
  }, [
    action,
    contextMenuSelection,
    setAction,
    showViewActiveOrderDialog,
    showEditDialog,
  ]);

  return (
    <div className="page orders-page">
      <HeaderPages
        title={t('Incomplete')}
        subtitle={t('View and manage incomplete orders')}
        icon={faCube}
      />

      <EditIncompleteDialog
        key={contextMenuSelection?.id}
        visible={isEditDialogVisible}
        data={contextMenuSelection}
        onHide={hideEditDialog}
        reload={reload}
        // isLoading={isActiveOrderDataLoading}
      />

      <ViewIncompleteOrderDialog
        visible={isViewActiveOrderDialogVisible}
        data={contextMenuSelection}
        // isLoading={isActiveOrderDataLoading}
        onHide={hideViewActiveOrderDialog}
        // {...(editGuard ? { onEdit: handleFromViewToEdit } : {})}
      />

      <Flex direction="column">
        <Filters
          filters={filters}
          resetAllFilters={resetAllFilters}
          headerFiltersCount={headerFiltersCount}
        />
        <MainContent>
          <Table
            tableRef={tableRef}
            columns={finalColumns}
            data={data}
            isLoading={isLoading}
            reload={reload}
            hasError={!!error}
            headerTitle=""
            setPage={setPage}
            setLimit={setLimit}
            sortField={sortField}
            rows={limit}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setSelection={setSelectionMultiple}
            sortOrder={sortOrder}
            selection={selectionMultiple}
            storageString={tableStorageKey}
            clearSelectionObj={httpFiltersObj}
            selectionPageOnly
            rebuildTooltip
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            columnOptions={columnOptions}
            contextMenuSelection={contextMenuSelection}
            setContextMenuSelection={setContextMenuSelection}
          />
        </MainContent>
      </Flex>
    </div>
  );
}
export default Incomplete;
